// Language Selector Component for kiosk interface
import React from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Language } from '../../types/language';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';

interface LanguageSelectorProps {
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  showLabel = true,
  size = 'md'
}) => {
  const { language, setLanguage, t } = useLanguage();

  const handleLanguageChange = (value: string) => {
    setLanguage(value as Language);
  };

  const sizeClasses = {
    sm: 'h-8 text-sm',
    md: 'h-12 text-base',
    lg: 'h-16 text-lg'
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {showLabel && (
        <Label htmlFor="language-select" className="text-kiosk-base font-medium">
          <Globe className="inline-block w-4 h-4 mr-2" />
          {t('language.label')}
        </Label>
      )}
      <Select value={language} onValueChange={handleLanguageChange}>
        <SelectTrigger 
          id="language-select"
          className={`${sizeClasses[size]} bg-white border-2 border-kiosk-border hover:border-kiosk-primary focus:border-kiosk-primary focus:ring-2 focus:ring-kiosk-primary/20 transition-colors`}
        >
          <SelectValue placeholder={t('language.label')} />
        </SelectTrigger>
        <SelectContent className="bg-white border-2 border-kiosk-border shadow-lg">
          <SelectItem 
            value="vi" 
            className="text-base py-3 px-4 hover:bg-kiosk-primary/10 focus:bg-kiosk-primary/10 cursor-pointer"
          >
            <div className="flex items-center">
              <span className="mr-2">🇻🇳</span>
              {t('language.vietnamese')}
            </div>
          </SelectItem>
          <SelectItem 
            value="en" 
            className="text-base py-3 px-4 hover:bg-kiosk-primary/10 focus:bg-kiosk-primary/10 cursor-pointer"
          >
            <div className="flex items-center">
              <span className="mr-2">🇺🇸</span>
              {t('language.english')}
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};